/**
 * Review, redesigned (docs/REVIEW-REDESIGN.md) — the rules shared by the
 * runtime (which applies actions) and the dashboard (which offers them).
 * Pure: no node built-ins, runs in the browser.
 */
import { z } from "zod";
/**
 * Resolution names. Vendored patch 0029 (SETTLED_ACTIONS) treats kept,
 * both-valid, merged, distinct, archived and created as settled: the same
 * question is not raised again. superseded / expired / researching are not.
 */
export const REVIEW_RESOLUTION = {
    kept: "kept",
    bothValid: "both-valid",
    merged: "merged",
    distinct: "distinct",
    archived: "archived",
    created: "created",
    researching: "researching",
    superseded: "superseded",
    expired: "expired",
};
export const REVIEW_SETTLED = ["kept", "both-valid", "merged", "distinct", "archived", "created"];
/** Ideas: a backlog to pick from. Everything else is a decision. */
export const REVIEW_IDEA_TYPES = ["suggestion", "missing-page"];
export const isReviewIdea = (type) => REVIEW_IDEA_TYPES.includes(type ?? "");
/** Ideas untouched this long expire. */
export const REVIEW_IDEA_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** A contradiction rewrites at most this many pages in one decision. */
export const REVIEW_MAX_REWRITE_PAGES = 6;
function normalizePosix(p) {
    const out = [];
    for (const seg of p.split("/")) {
        if (seg === "" || seg === ".")
            continue;
        if (seg === "..") {
            if (out.length === 0)
                return null;
            out.pop();
        }
        else
            out.push(seg);
    }
    return out.join("/");
}
/** A cited page as a project-relative wiki/ or raw/ path, or null if it escapes. */
export function reviewRelPage(p) {
    let r = p.trim().replace(/\\/g, "/").replace(/^\/+/, "");
    if (r.startsWith("[[") && r.endsWith("]]"))
        r = r.slice(2, -2);
    r = r.replace(/^(?:\.\/)+/, "");
    if (!r.startsWith("wiki/") && !r.startsWith("raw/"))
        r = `wiki/${r}`;
    if (!/\.[a-z0-9]+$/i.test(r))
        r = `${r}.md`;
    const norm = normalizePosix(r);
    if (!norm || (!norm.startsWith("wiki/") && !norm.startsWith("raw/")))
        return null;
    // Review actions read, compare and rewrite text: binary files never qualify.
    // Wiki pages are .md (what merge, slugs and embeddings handle); sources may be any text.
    if (norm.startsWith("wiki/") ? !/\.md$/i.test(norm) : !/\.(?:md|markdown|txt)$/i.test(norm))
        return null;
    return norm;
}
export function reviewCitedPages(item) {
    const out = [];
    for (const p of item.affectedPages ?? []) {
        const r = reviewRelPage(p);
        if (r && !out.includes(r))
            out.push(r);
    }
    return out;
}
/**
 * The item's source as a project-relative path, when it is a readable text
 * file. Ingest stores it absolute: with `projectRoot` (server side) the root
 * is stripped exactly; without it (browser) the first wiki/ or raw/ segment
 * is taken — the server re-checks every target against its own resolution.
 */
export function reviewSourceRel(item, projectRoot) {
    const sp = (item.sourcePath ?? "").replace(/\\/g, "/");
    let rel = null;
    const root = projectRoot?.replace(/\\/g, "/").replace(/\/+$/, "");
    // Absolute in any form: /x, //unc/x, C:/x.
    const absolute = /^(?:\/|[a-zA-Z]:\/)/.test(sp);
    if (root) {
        // Drive-letter roots compare case-insensitively (Windows); others exactly.
        const fold = /^[a-zA-Z]:\//.test(root) ? (x) => x.toLowerCase() : (x) => x;
        if (fold(sp).startsWith(`${fold(root)}/`))
            rel = sp.slice(root.length + 1);
        else if (absolute)
            return null;
        else
            rel = /(?:^|\/)((?:wiki|raw)\/.*)$/.exec(sp)?.[1] ?? null;
    }
    else {
        rel = /(?:^|\/)((?:wiki|raw)\/.*)$/.exec(sp)?.[1] ?? null;
    }
    if (!rel)
        return null;
    const r = reviewRelPage(rel);
    return r && /\.(?:md|markdown|txt)$/i.test(r) ? r : null;
}
/** The sides of a contradiction: cited pages plus the source that raised it. */
export function reviewSides(item, projectRoot) {
    const out = reviewCitedPages(item);
    const src = reviewSourceRel(item, projectRoot);
    if (src && !out.includes(src))
        out.push(src);
    return out;
}
export const ReviewActSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("keep"), target: z.string().min(1) }),
    z.object({ action: z.literal("both-valid") }),
    z.object({ action: z.literal("merge"), target: z.string().min(1) }),
    z.object({ action: z.literal("distinct") }),
    z.object({ action: z.literal("archive") }),
    /** Ideas: write a starter page (never over an existing one). */
    z.object({ action: z.literal("create-page") }),
    /** Records that a deep research was started for the item (the client starts it). */
    z.object({ action: z.literal("research") }),
]);
export function reviewActionsFor(item, projectRoot) {
    switch (item.type) {
        case "contradiction":
            return [
                ...reviewSides(item, projectRoot)
                    .slice(0, REVIEW_MAX_REWRITE_PAGES + 1)
                    .map((target) => ({ action: "keep", target })),
                { action: "both-valid" },
                { action: "research" },
            ];
        case "duplicate":
            return [
                ...reviewCitedPages(item)
                    .filter((p) => p.startsWith("wiki/"))
                    .map((target) => ({ action: "merge", target })),
                { action: "distinct" },
            ];
        case "suggestion":
        case "missing-page":
            return [{ action: "research" }, { action: "create-page" }, { action: "archive" }];
        default:
            // e.g. legacy `confirm`: no phase-1 action (the resolve API still works).
            return [];
    }
}
export const ReviewLogEntrySchema = z.object({
    id: z.string(),
    at: z.number(),
    /** `auto` = done by the system; `decision` = a user choice the system applied. */
    kind: z.enum(["auto", "decision"]),
    itemId: z.string(),
    itemType: z.string(),
    itemTitle: z.string(),
    action: z.string(),
    note: z.string(),
    /** Every file touched, with the sha256 left on disk (null = absent afterwards). */
    changes: z.array(z.object({ path: z.string(), afterHash: z.string().nullable(), beforeHash: z.string().nullable() })),
    /** Registry change made by this entry (undone with it). */
    registry: z.object({ notDuplicate: z.array(z.string()) }).optional(),
    undoneAt: z.number().optional(),
    /**
     * Write-ahead journal: set before an apply / undo touches anything, cleared
     * when it completes. A pending entry found at start-up is recovered.
     * `revert`: an apply that failed and whose compensation did not finish —
     * recovery rolls it back (never completes it).
     */
    pending: z.enum(["apply", "undo", "revert"]).optional(),
    /** Deep research queued for the item (idempotency: never queued twice). */
    taskId: z.string().optional(),
    /** When that research finished (a research never finished is reopened at start-up). */
    researchDone: z.number().optional(),
    /**
     * Files the action was computed from but did not write, with the sha256 they
     * had (null = absent): recovery completes the entry only if they still match.
     */
    inputs: z.array(z.object({ path: z.string(), hash: z.string().nullable() })).optional(),
    /** For a merge: digest of every other wiki page the plan was computed from. */
    inputsWikiDigest: z.string().optional(),
    /** Fingerprint of the question when the action started (never resolve a changed one). */
    itemFingerprint: z.string().optional(),
    /** Recovery could not finish: these files hold content neither old nor new (left untouched). */
    conflict: z.array(z.string()).optional(),
});
export const ReverifyStatusSchema = z.object({
    started: z.boolean(),
    running: z.boolean(),
    /** false: no usable LLM — only the expiry of old ideas ran. */
    llmAvailable: z.boolean(),
});
export const ReverifyResultSchema = z.object({
    checked: z.number(),
    closed: z.number(),
    expired: z.number(),
    failed: z.number(),
});
//# sourceMappingURL=review.js.map